import { FC, useRef, useEffect } from "react";
import { formatTimeSec } from "../../exam/TimeSecInput";
import Linkify from "linkify-react";
import type { ApiExamAnswerApi } from "../../../generated/api";

interface Props {
  feedback: Required<
    Awaited<ReturnType<ApiExamAnswerApi["apiVideoVideoIdExamAnswersPost"]>>
  >;
}

export const AnswerFeedback: FC<Props> = ({ feedback }) => {
  const elemRef = useRef<HTMLDivElement>(null);

  // 回答結果の描画時に見える位置にスクロールさせる
  useEffect(() => {
    elemRef.current?.scrollIntoView?.();
  }, [feedback]);

  const rate = Math.round(
    (feedback.correctAnswerCount / feedback.totalQuestionCount) * 100
  );
  return (
    <div className="p-exam-answer-feedback" ref={elemRef}>
      <p className="p-exam-answer-feedback__title">
        テストの回答結果
        <span className="p-exam-answer-feedback__score">
          {feedback.correctAnswerCount}/{feedback.totalQuestionCount}
          問正解(正解率 {rate}%)
        </span>
      </p>
      {feedback.questionAnswers.map((question, index) => (
        <AnswerFeedbackQuestion
          key={index}
          questionNo={index + 1}
          question={question}
        />
      ))}
    </div>
  );
};

export default AnswerFeedback;

function AnswerFeedbackQuestion(props: {
  questionNo: number;
  question: Props["feedback"]["questionAnswers"][number];
}) {
  const referenceTimeText = formatTimeSec(props.question.referenceVideoTimeSec);
  const showExplanation =
    !!props.question.explanation ||
    Number.isSafeInteger(props.question.referenceVideoTimeSec);

  function playReferenceVideoTime() {
    const timeSec = props.question.referenceVideoTimeSec;
    if (Number.isSafeInteger(timeSec) && timeSec >= 0) {
      const video = document.querySelector("video");
      if (!video) {
        return; // ビデオの再生準備前などでプレイヤーが描画されていない場合がある
      }
      video.scrollIntoView({ behavior: "smooth", block: "center" });
      video.addEventListener("seeked", () => video.play(), { once: true });
      video.currentTime = timeSec;
    }
  }

  return (
    <div
      className={`p-exam-answer-result p-exam-answer-result--${
        props.question.correct ? "correct" : "incorrect"
      }`}
    >
      <h3 className="p-exam-answer-result__heading">
        <span
          className={`c-exam-answer-correctness c-exam-answer-correctness--${
            props.question.correct ? "correct" : "incorrect"
          }`}
        >
          {props.question.correct ? "◯" : "×"}
        </span>
        問題 {props.questionNo}
      </h3>
      <p className="p-exam-answer-result__text">{props.question.question}</p>
      <div className="p-exam-answer-result__options">
        {props.question.options.map((optionText, index) => (
          <label key={index} className="p-exam-answer-result__options-item">
            <input
              className={`p-exam-answer-result__options-radio p-exam-answer-result__options-radio--${
                props.question.correct ? "correct" : "incorrect"
              }`}
              type="radio"
              readOnly
              disabled={props.question.answerText !== optionText}
              checked={props.question.answerText === optionText}
              name={`question-${props.questionNo}`}
            />
            {optionText}
          </label>
        ))}
      </div>
      {showExplanation && (
        <div
          className={`p-exam-answer-explanation p-exam-answer-explanation--${
            props.question.correct ? "correct" : "incorrect"
          }`}
        >
          <p className="p-exam-answer-explanation__heading">
            <span className="material-icons-outlined p-exam-answer-explanation__heading-icon">
              info
            </span>
            解説
          </p>
          <div className="p-exam-answer-explanation__content">
            {props.question.explanation && (
              <p className="p-exam-answer-explanation__text">
                <Linkify
                  options={{
                    target: "_blank",
                    // 自動リンクするのはHTTPのURLのみ
                    email: false,
                    phone: false,
                    validate: { url: (url) => /^https?:\/\//.test(url) },
                  }}
                >
                  {props.question.explanation}
                </Linkify>
              </p>
            )}
            {referenceTimeText && (
              <button
                onClick={playReferenceVideoTime}
                className="c-button--outlined"
              >
                ▶︎ ビデオ内での解説（{referenceTimeText}〜）
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
